import React from "react";
import { formatDate } from "../../shared/helpers/formatters";
import ToolsInlineEdit from "../../../kit/ToolsInlineEdit";
import ToolsEditableParagraphs from "../../../kit/ToolsEditableParagraphs";
import BookkeepingService from "../../../../../../services/BookkeepingService";
import "../../afs-walkthrough.css";

const fmtDate = formatDate;

// ─── Compilation Letter View (CSRS 4200 standard report) ─────────────────────
// Shows only the compilation engagement report letter, matching the CaseWare PDF
// format exactly. The text follows CSRS 4200 paragraphs with dynamic entity/date fields.

function CompilationLetterView({ compilationData, compilationReportSignature, compilationReport, entityName, clientProfile, yearEnd, statementEntityName, onChangeStatementSetting }) {
	const signature    = compilationReportSignature || {};
	const entity       = statementEntityName || (clientProfile && clientProfile.entityName) || entityName || "[Entity Name]";
	const firm         = signature.firmName || "";
	const firmCity     = signature.city || "";
	const reportDate   = signature.reportDate ? fmtDate(signature.reportDate) : "";

	const report          = compilationReport || {};
	const isCustomWording = !!report.isCustom;
	const canEditWording  = !!onChangeStatementSetting;

	const paragraphs = report.paragraphs || [];

	const onWordingModeChange = ({ custom }) => {
		const settings = BookkeepingService.describeWordingModeChange({ custom, report });
		onChangeStatementSetting({ settings });
	};

	const onParagraphsCommitted = ({ paragraphs: rewritten }) => {
		onChangeStatementSetting({ settings: { compilationReportParagraphs: rewritten } });
	};

	return (
		<div className="afs-letter">
			<h2 className="afs-letter__title">Compilation Engagement Report</h2>
			<p className="afs-letter__addressee">
				To the Management of{" "}
				<ToolsInlineEdit
					value={entity}
					placeholder="Add the name the statements are headed with"
					label="the name on the statements"
					disabled={!onChangeStatementSetting}
					onChange={({ value }) => onChangeStatementSetting({ setting: "statementEntityName", value })}
				/>
			</p>
			{canEditWording && (
				<div className="afs-letter__wording-mode">
					<button
						type="button"
						className={"afs-letter__wording-choice" + (isCustomWording ? "" : " afs-letter__wording-choice--on")}
						onClick={() => onWordingModeChange({ custom: false })}
					>
						Default wording
					</button>
					<button
						type="button"
						className={"afs-letter__wording-choice" + (isCustomWording ? " afs-letter__wording-choice--on" : "")}
						onClick={() => onWordingModeChange({ custom: true })}
					>
						Custom wording
					</button>
					{isCustomWording && (
						<span className="afs-letter__wording-note">Reworded for this engagement — the standard text is kept and restored by choosing Default.</span>
					)}
				</div>
			)}

			{isCustomWording && canEditWording ? (
				<ToolsEditableParagraphs
					key="custom"
					paragraphs={paragraphs}
					paragraphClassName="afs-letter__paragraph afs-letter__paragraph--editable"
					onCommit={onParagraphsCommitted}
				/>
			) : (
				paragraphs.map((paragraph, index) => (
					<p key={index} className="afs-letter__paragraph">{paragraph}</p>
				))
			)}
			<div className="afs-letter__signature">
				<p className="afs-letter__sig-line">
					<ToolsInlineEdit
						value={firmCity}
						placeholder="Add the city the report is signed in"
						label="the city this report is signed in"
						disabled={!onChangeStatementSetting}
						onChange={({ value }) => onChangeStatementSetting({ setting: "compilationReportCity", value })}
					/>
				</p>
				{firm && <p className="afs-letter__sig-bold">{firm}</p>}
				<p className="afs-letter__sig-line">
					<ToolsInlineEdit
						value={signature.reportDate || ""}
						displayValue={reportDate}
						type="date"
						placeholder="Add a report date"
						label="the report date"
						disabled={!onChangeStatementSetting}
						onChange={({ value }) => onChangeStatementSetting({ setting: "compilationReportDate", value })}
					/>
				</p>
				<p className="afs-letter__sig-designation">Chartered Professional Accountants</p>
			</div>
		</div>
	);
}

export default CompilationLetterView;
